import { BarcodeType, ProductLine, ScreenType } from 'types/index';
import {
  BARCODE_SCREEN_TYPES_LIST,
  BARCODE_TYPES,
  PRODUCT_CONDITION_TYPES,
  SCREEN_TYPES,
} from 'utils/constants';
import { submitCurrentGradeItem } from 'utils/gradeActions';
import { closeCurrentReceipt } from 'utils/closeReceiptAction';
import { useAppStore } from 'store/appStore';
import { useProductStore } from 'store/productStore';
import { useArrivalReceiptStore } from 'store/arrivalReceiptStore';
import { handleProblemResolve } from 'utils/handleProblem';
import { handleCloseReceipt, handleScanItemPageOpened } from 'utils/index';

interface BarcodeActionDependencies {
  setProductNew: (isNew: boolean) => void;
  setProductCondition: (condition: string) => void;
  setBarcodeDebugMessage: (message: string) => void;
  setProductId: (id: string) => void;
  setTrackingId: (id: string) => void;
  setCurrentProduct: (product: ProductLine | null) => void;
}

export const isInputBarcode = (value: string): BarcodeType | null => {
  const matched = BARCODE_SCREEN_TYPES_LIST.find((b) => b.name === value);
  return matched ? matched.name : null;
};

const cancelActionForScreen = (
  currentScreen: ScreenType,
  dependencies: BarcodeActionDependencies
) => {
  const { setProductId, setTrackingId, setProductNew, setProductCondition, setCurrentProduct } =
    dependencies;
  const { setCurrentScreen } = useAppStore.getState();

  switch (currentScreen) {
    case SCREEN_TYPES.SCAN_ITEM:
      setProductId('');
      setTrackingId('');
      setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
      break;
    case SCREEN_TYPES.CONFIRM_ITEM:
      handleScanItemPageOpened();
      setProductId('');
      setCurrentProduct(null);
      break;
    case SCREEN_TYPES.GRADE_ITEM:
      setCurrentScreen(SCREEN_TYPES.CONFIRM_ITEM);
      break;
    case SCREEN_TYPES.DISPOSITION:
      setCurrentScreen(SCREEN_TYPES.GRADE_ITEM);
      setProductNew(null);
      setProductCondition(null);
      break;
    case SCREEN_TYPES.CONFIRM_CLOSE:
      setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
      break;
    case SCREEN_TYPES.RECEIVING_DISPOSITION:
      useArrivalReceiptStore.getState().setCurrentArrivalReceipt(null);
      setCurrentScreen(SCREEN_TYPES.RECEIVING);
      break;
    case SCREEN_TYPES.SCAN_BOX:
    default:
      break;
  }
};

export const getBarcodeAction = (
  value: BarcodeType,
  currentScreen: ScreenType,
  dependencies: BarcodeActionDependencies
) => {
  const { setProductNew, setProductCondition, setBarcodeDebugMessage } = dependencies;
  const barcode = BARCODE_SCREEN_TYPES_LIST.find((b) => b.name === value);

  if (!barcode) {
    setBarcodeDebugMessage(`Barcode not found: ${value}`);
    return null;
  }

  const allowedScreen = barcode.screen;

  if (allowedScreen !== 'any' && allowedScreen !== currentScreen) {
    setBarcodeDebugMessage(`Barcode ${value} not valid on screen ${currentScreen}`);
    return null;
  }

  switch (value) {
    case BARCODE_TYPES.NEW_YES:
      setBarcodeDebugMessage('Setting product as new');
      setProductNew(true);
      break;
    case BARCODE_TYPES.NEW_NO:
      setBarcodeDebugMessage('Setting product as used');
      setProductNew(false);
      break;
    case BARCODE_TYPES.PACKAGING_YES:
      setBarcodeDebugMessage('Setting product condition as has packaging');
      setProductCondition(PRODUCT_CONDITION_TYPES.HAS_PACKAGING);
      break;
    case BARCODE_TYPES.PACKAGING_NO:
      setBarcodeDebugMessage('Setting product condition as no packaging');
      setProductCondition(PRODUCT_CONDITION_TYPES.NO_PACKAGING);
      break;
    case BARCODE_TYPES.CONDITION_USABLE:
      setBarcodeDebugMessage('Setting product condition as usable');
      setProductCondition(PRODUCT_CONDITION_TYPES.USABLE);
      break;
    case BARCODE_TYPES.CONDITION_UNUSABLE:
      setBarcodeDebugMessage('Setting product condition as unusable');
      setProductCondition(PRODUCT_CONDITION_TYPES.UNUSABLE);
      break;
    case BARCODE_TYPES.FINISH:
      setBarcodeDebugMessage('Finishing current VSR');
      // Triggers close out for current VSR
      handleCloseReceipt();
      break;
    case BARCODE_TYPES.CANCEL:
      setBarcodeDebugMessage('Cancelling current action');
      // Cancels current action
      cancelActionForScreen(currentScreen, dependencies);
      break;
    case BARCODE_TYPES.PROBLEM:
      setBarcodeDebugMessage('Handling problem resolve');
      void handleProblemResolve(currentScreen);
      break;
    case BARCODE_TYPES.SUBMIT:
      setBarcodeDebugMessage('Submitting current grade item');
      void submitCurrentGradeItem();
      break;
    case BARCODE_TYPES.CONFIRM:
      setBarcodeDebugMessage('Confirming item');
      useAppStore.getState().setCurrentScreen(SCREEN_TYPES.GRADE_ITEM);
      break;
    case BARCODE_TYPES.REJECT:
      setBarcodeDebugMessage('Toggling reject');
      useProductStore.getState().toggleIsRejected();
      break;
    case BARCODE_TYPES.CLOSE:
      setBarcodeDebugMessage('Closing receipt');
      void closeCurrentReceipt();
      break;
    default:
      console.log('Unhandled barcode value:', value);
  }
};
